// =============================================================
// Live ticking SLA countdown
// =============================================================
import { useEffect, useState, type ComponentType } from "react";
import {
  Timer,
  AlertTriangle,
  CheckCircle2,
  type LucideProps,
} from "lucide-react";
import { fmtCountdown } from "../../lib/format";
import { SLA_HOURS } from "../../lib/constants";
import type { Priority, Status } from "../../lib/api";
import { cn } from "../../lib/cn";

type CountdownState = "ok" | "at-risk" | "breached" | "met" | "unknown";

function computeState(
  deadline: string | null | undefined,
  priority: Priority,
): CountdownState {
  if (!deadline) return "unknown";
  const ms = new Date(deadline).getTime() - Date.now();
  if (ms < 0) return "breached";
  const totalMs = (SLA_HOURS[priority] ?? SLA_HOURS.MEDIUM) * 3_600_000;
  if (ms < totalMs * 0.25) return "at-risk";
  return "ok";
}

const STATE_STYLES: Record<
  CountdownState,
  { text: string; bg: string; border: string; icon: ComponentType<LucideProps>; label: string }
> = {
  ok:        { text: "text-emerald-200",  bg: "bg-emerald-500/15",  border: "border-emerald-400/40",  icon: Timer,         label: "On track" },
  "at-risk": { text: "text-amber-200",    bg: "bg-amber-500/15",    border: "border-amber-400/40",    icon: AlertTriangle, label: "At risk" },
  breached:  { text: "text-critical-200", bg: "bg-critical-500/15", border: "border-critical-400/40", icon: AlertTriangle, label: "Breached" },
  met:       { text: "text-emerald-200",  bg: "bg-emerald-500/15",  border: "border-emerald-400/40",  icon: CheckCircle2,  label: "Met" },
  unknown:   { text: "text-slate-300",    bg: "bg-white/5",         border: "border-white/10",        icon: Timer,         label: "No SLA" },
};

export function SLACountdown({
  deadline,
  priority = "MEDIUM",
  status,
  compact = false,
  className,
}: {
  deadline?: string | null;
  priority?: Priority;
  status?: Status;
  compact?: boolean;
  className?: string;
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1_000);
    return () => clearInterval(id);
  }, []);

  if (status && ["RESOLVED", "CLOSED"].includes(status)) {
    const s = STATE_STYLES.met;
    const Icon = s.icon;
    return (
      <span className={cn("badge", s.text, s.bg, s.border, className)}>
        <Icon className="w-3 h-3" />
        {s.label}
      </span>
    );
  }

  const state = computeState(deadline, priority);
  const { label: countdown, breached } = fmtCountdown(deadline);
  const s = STATE_STYLES[state];
  const Icon = s.icon;

  if (compact) {
    return (
      <span className={cn("badge", s.text, s.bg, s.border, className)}>
        <Icon className="w-3 h-3" />
        {breached ? `Breached ${countdown}` : countdown}
      </span>
    );
  }

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-xl px-3 py-1.5 border",
        s.text,
        s.bg,
        s.border,
        state === "breached" && "animate-pulse-glow",
        className,
      )}
    >
      <Icon className="w-4 h-4" />
      <div className="leading-tight">
        <div className="text-[10px] uppercase tracking-wider font-semibold opacity-80">
          {s.label}
        </div>
        <div className="text-sm font-mono font-semibold">
          {breached ? `+${countdown.replace("+", "")}` : countdown}
        </div>
      </div>
    </div>
  );
}
